import { ref, computed, type ComputedRef, type Ref } from 'vue'
import axios from 'axios'
import { isAdminRole, type UserRole } from '@/types/api'

// =============================================================================
// Types
// =============================================================================

interface UserSession {
    accessToken: string
    user: {
        id: number
        email: string
        name: string | null
        role: UserRole
        /**
         * Free-form profile extras. Present on GET /auth/me, absent from the
         * login response, so treat every field as optional.
         */
        payload?: Record<string, unknown>
    }
}

interface UseAuthReturn {
    currentUser: ComputedRef<UserSession | null>
    user: ComputedRef<UserSession['user'] | null>
    token: ComputedRef<string>
    isAuthenticated: ComputedRef<boolean>
    userRole: ComputedRef<UserRole | ''>
    isAdmin: ComputedRef<boolean>
    login: (email: string, password: string) => Promise<UserSession>
    logout: () => void
    refresh: () => Promise<string>
    updateUser: (updatedUser: Partial<UserSession>) => void
    forgotPassword: (email: string) => Promise<{ success: boolean; message: string }>
}

// =============================================================================
// Shared State (Singleton)
// =============================================================================

const currentUser: Ref<UserSession | null> = ref(null)
const isAuthenticated = ref(false)

// Initialize from localStorage
const storedUser = localStorage.getItem('user')
if (storedUser) {
    try {
        const parsed = JSON.parse(storedUser) as UserSession
        currentUser.value = parsed
        isAuthenticated.value = true
        axios.defaults.headers.common['Authorization'] = `Bearer ${parsed.accessToken}`
    } catch {
        localStorage.removeItem('user')
    }
}

// =============================================================================
// useAuth Composable
// =============================================================================

export function useAuth(): UseAuthReturn {
    const token = computed(() => currentUser.value?.accessToken || '')
    const user = computed(() => currentUser.value?.user || null)
    const userRole = computed<UserRole | ''>(() => currentUser.value?.user?.role || '')
    const isAdmin = computed(() => isAdminRole(userRole.value))

    const login = async (email: string, password: string): Promise<UserSession> => {
        try {
            const response = await axios.post('auth/login', { email, password })

            // Backend returns { success: true, data: { token, user } }
            const loginData = response?.data?.data || response?.data

            if (loginData?.token) {
                const session: UserSession = {
                    accessToken: loginData.token,
                    user: loginData.user
                }

                currentUser.value = session
                isAuthenticated.value = true

                localStorage.setItem('user', JSON.stringify(session))
                axios.defaults.headers.common['Authorization'] = `Bearer ${loginData.token}`

                return session
            }

            throw new Error('Invalid response from server')
        } catch (error) {
            isAuthenticated.value = false
            currentUser.value = null
            throw error
        }
    }

    const logout = (): void => {
        currentUser.value = null
        isAuthenticated.value = false
        localStorage.removeItem('user')
        delete axios.defaults.headers.common['Authorization']
    }

    const refresh = async (): Promise<string> => {
        try {
            const response = await axios.post('auth/refresh')
            const newToken = response?.data?.token

            if (newToken && currentUser.value) {
                currentUser.value.accessToken = newToken
                localStorage.setItem('user', JSON.stringify(currentUser.value))
                axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`
                return newToken
            }

            throw new Error('No token received')
        } catch (error) {
            logout()
            throw error
        }
    }

    const updateUser = (updatedUser: Partial<UserSession>): void => {
        if (currentUser.value) {
            currentUser.value = { ...currentUser.value, ...updatedUser }
            localStorage.setItem('user', JSON.stringify(currentUser.value))
        }
    }

    const forgotPassword = async (email: string): Promise<{ success: boolean; message: string }> => {
        const response = await axios.post('auth/forgot-password', { email })
        return response.data
    }

    return {
        // State
        currentUser: computed(() => currentUser.value),
        user,
        token,
        isAuthenticated: computed(() => isAuthenticated.value),
        userRole,
        isAdmin,

        // Actions
        login,
        logout,
        refresh,
        updateUser,
        forgotPassword
    }
}
